import { Box, Button, CircularProgress } from '@mui/material';
import { FunctionComponent, useState, useCallback, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getEventAudits } from '@context/inspections/inspectionUtils';

import './index.scss';

interface ClaimStepsProps {
  claim: any;
  eventId?: string;
}

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'white',
    color: '#6D788B',
    fontStyle: 'normal',
    fontSize: '14px',
    letterSpacing: '0.5px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: '600',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const History: FunctionComponent<ClaimStepsProps> = ({
  claim,
  eventId,
}): JSX.Element => {
  const [historyItems, setHistoryItems] = useState<any[]>(claim || []);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Si hay exactamente 10 registros iniciales, asumimos que puede haber más
  const [hasMore, setHasMore] = useState<boolean>(claim && claim.length === 10);
  const [loading, setLoading] = useState<boolean>(false);

  const formatDate = (dateString: string) => {
    dayjs.extend(utc);
    const date = dayjs(dateString);
    const formattedDate = date
      .utc(true)
      .local()
      .format('D MMMM YYYY, h:mm:ss A');

    return formattedDate;
  };

  const loadMoreAudits = useCallback(async () => {
    if (!eventId || loading) return;

    setLoading(true);
    try {
      const nextPage = currentPage + 1;
      const response = await getEventAudits(eventId, nextPage);

      console.log('Response from getEventAudits:', response);

      if (response && response.audits && Array.isArray(response.audits)) {
        console.log('Adding audits to history:', response.audits);
        setHistoryItems((prev) => {
          const updated = [...prev, ...response.audits];
          console.log('Updated historyItems length:', updated.length);
          return updated;
        });
        setCurrentPage(nextPage);
        setHasMore(response.hasMore !== false); // Asegurar que hasMore sea boolean
      } else {
        console.warn('Invalid response structure:', response);
      }
    } catch (error) {
      console.error('Error loading more audits:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId, currentPage, loading]);

  // Actualizar historyItems cuando cambie claim
  useEffect(() => {
    if (claim) {
      setHistoryItems(claim);
      setCurrentPage(1); // Resetear a la página 1 cuando cambie el claim
      // Si hay exactamente 10 registros, asumimos que puede haber más
      setHasMore(claim.length === 10);
    }
  }, [claim]);

  return (
    <Box sx={{ paddingTop: '1rem' }}>
      <label>Historial del caso</label>
      <TableContainer component={Paper} className="TableContainer">
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Nombre</StyledTableCell>
              <StyledTableCell align="left">Email</StyledTableCell>
              <StyledTableCell align="left">Fecha</StyledTableCell>
              <StyledTableCell align="left">Descripción</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historyItems.length > 0 ? (
              historyItems.map((row: any, index: any) => (
                <StyledTableRow key={`${index}-${row.name}-${row.createdDate}`}>
                  <StyledTableCell component="th" scope="row">
                    {row.name}
                  </StyledTableCell>
                  <StyledTableCell align="left">{row.email}</StyledTableCell>
                  <StyledTableCell align="left">
                    {formatDate(row.createdDate)}
                  </StyledTableCell>
                  <StyledTableCell align="left">{row.message}</StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell colSpan={4} align="center">
                  <p>Sin información</p>
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {hasMore && eventId && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <Button
            variant="outlined"
            onClick={loadMoreAudits}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Cargando...' : 'Ver más'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default History;
